/**
 * Structural reference checks for the reference audit.
 *
 * Package-private to the `referenceAudit*` module family. Covers:
 *   - style-table refs (DimStyle DIMTXSTY / DIMBLK*, MLeaderStyle textStyle /
 *     arrowhead) — `checkStyleTableRefs`.
 *   - handle refs on block defs / block records / layouts / document —
 *     `checkHandleRefs`.
 *   - block-reference cycles via DFS over the block → INSERT graph —
 *     `checkBlockReferenceCycles` (+ `collectInsertChildren`).
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §4 / §5.3 / §7.
 */

import type { BlockDefinition, DrawingDocument } from "./model";
import {
  checkHandle,
  checkMleaderArrowheadRef,
  checkName,
} from "./referenceAudit.checkers";
import type { Resolution } from "./referenceAudit.resolution";
import {
  SENT_ARROWHEAD,
  SENT_LINETYPE,
  SENT_NONE,
} from "./referenceAudit.sentinels";
import {
  ReferenceErrorCode,
  type ReferenceIssue,
} from "./referenceAudit.types";

/* ────────────────────────────────────────────────────────────────────────
 * Style-table reference checks (DimStyle / MLeaderStyle).
 * ──────────────────────────────────────────────────────────────────────── */

export function checkStyleTableRefs(
  doc: DrawingDocument,
  res: Resolution,
  errors: ReferenceIssue[],
): void {
  // Layer.linetype → linetypes (field 46, required). Enabled after PR-LT2
  // (plan §10.5). `SENT_LINETYPE` bypasses ByLayer/ByBlock/Continuous.
  for (const layer of doc.styleTables.layers) {
    checkName(
      layer.linetype,
      res.names.linetypes,
      SENT_LINETYPE,
      true,
      ReferenceErrorCode.UNDEFINED_LINETYPE,
      {
        ownerKind: "styleTableRecord",
        ownerId: layer.name,
        path: "linetype",
        field: "linetype",
      },
      errors,
    );
  }

  for (const ds of doc.styleTables.dimensionStyles) {
    // DIMTXSTY → textStyles (required).
    checkName(
      ds.DIMTXSTY,
      res.names.textStyles,
      SENT_NONE,
      true,
      ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
      {
        ownerKind: "styleTableRecord",
        ownerId: ds.name,
        path: "DIMTXSTY",
        field: "DIMTXSTY",
      },
      errors,
    );
    // DIMBLK / DIMBLK1 / DIMBLK2 / DIMLDRBLK → blocks (optional, arrowhead).
    for (const field of ["DIMBLK", "DIMBLK1", "DIMBLK2", "DIMLDRBLK"] as const) {
      checkName(
        ds[field],
        res.names.blocks,
        SENT_ARROWHEAD,
        false,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        {
          ownerKind: "styleTableRecord",
          ownerId: ds.name,
          path: field,
          field,
        },
        errors,
      );
    }
  }

  for (const ms of doc.styleTables.multileaderStyles) {
    // textStyleName → textStyles (required).
    checkName(
      ms.textStyleName,
      res.names.textStyles,
      SENT_NONE,
      true,
      ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
      {
        ownerKind: "styleTableRecord",
        ownerId: ms.name,
        path: "textStyleName",
        field: "textStyleName",
      },
      errors,
    );
    // arrowheadBlock → blocks (optional). MLEADERSTYLE resolves the arrowhead NAME
    // to a 341 handle on export (`resolveMLeaderStyleReferenceHandles`) and DROPS the
    // group when the NAME has no live BLOCK_RECORD — with NO built-in synthesis
    // (unlike DIMENSION DSTYLE). So only the closed-filled default (`_ClosedFilled` /
    // `"0"`) or a value LITERALLY matching a real block NAME resolves clean. SL-11
    // round-7 (@codex F3): unlike the MLEADER-ENTITY body-342 (written verbatim, a
    // live handle round-trips), the `"style"` site resolves by NAME only, so a raw
    // BLOCK_RECORD handle value would be DROPPED → dangles → it too is flagged; an
    // unprefixed built-in like `OPEN90` with no block also dangles → flagged.
    checkMleaderArrowheadRef(
      ms.arrowheadBlock,
      res.names.blocks,
      res.handles.blockRecords,
      "style", // 341 resolved NAME→handle → a raw handle is dropped → flag it
      ReferenceErrorCode.UNDEFINED_BLOCK,
      {
        ownerKind: "styleTableRecord",
        ownerId: ms.name,
        path: "arrowheadBlock",
        field: "arrowheadBlock",
      },
      errors,
    );
    // blockName → blocks (block-content style content reference). This is a
    // REAL block ref, NOT an arrowhead token (Codex #199 P2: a content
    // blockName of "_ClosedFilled" must NOT bypass — use SENT_NONE so any
    // non-empty name must resolve to a real block definition).
    checkName(
      ms.blockName,
      res.names.blocks,
      SENT_NONE,
      // Required for block-content styles (contentType 1): an empty blockName
      // leaves the style with no content to render (Codex #199 P2). Optional
      // for text-content styles (contentType 2).
      ms.contentType === 1,
      ReferenceErrorCode.UNDEFINED_BLOCK,
      {
        ownerKind: "styleTableRecord",
        ownerId: ms.name,
        path: "blockName",
        field: "blockName",
      },
      errors,
    );
    // leaderLineType → linetypes (field 35, optional). Enabled after PR-LT2
    // (plan §10.5). The schema default is "ByBlock" (a SENT_LINETYPE sentinel),
    // so the common case bypasses; a custom non-empty name must resolve.
    checkName(
      ms.leaderLineType,
      res.names.linetypes,
      SENT_LINETYPE,
      false,
      ReferenceErrorCode.UNDEFINED_LINETYPE,
      {
        ownerKind: "styleTableRecord",
        ownerId: ms.name,
        path: "leaderLineType",
        field: "leaderLineType",
      },
      errors,
    );
  }
}

/* ────────────────────────────────────────────────────────────────────────
 * Complex-LTYPE StyleRef refchecks (text/shape element → STYLE table).
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Each complex (text/shape) LTYPE element carries a STYLE reference (Codex#1
 * F11): the resolved `styleName` must point at a real text style. We validate
 * `styleName` → `textStyles` (case-insensitive). The raw `styleHandle` is
 * preserved for round-trip but is NOT verifiable here (the drawing-model
 * TextStyle has no handle field), so an element that carries ONLY an
 * unresolved handle is reported via its `shxResolved: false` render state,
 * NOT as a hard reference error. Optional / report-only: an empty StyleRef is
 * a dirty-but-loadable record, never a throw (F12).
 */
export function checkLinetypeStyleRefs(
  doc: DrawingDocument,
  res: Resolution,
  errors: ReferenceIssue[],
): void {
  const linetypes = doc.linetypes;
  if (!linetypes) return;
  for (const [key, lt] of Object.entries(linetypes)) {
    lt.elements.forEach((el, i) => {
      if (el.kind !== "text" && el.kind !== "shape") return;
      // Only a PRESENT-but-unresolved styleName is an error; a missing
      // styleName (handle-only / no ref) is the shxResolved=false state.
      if (el.styleName === undefined || el.styleName === "") return;
      checkName(
        el.styleName,
        res.names.textStyles,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
        {
          ownerKind: "linetype",
          ownerId: lt.name,
          path: `linetypes[${JSON.stringify(key)}].elements[${i}].styleName`,
          field: "linetypeElement.styleName",
        },
        errors,
      );
    });
  }
}

/* ────────────────────────────────────────────────────────────────────────
 * Handle reference checks (block defs, block records, layouts, document).
 * ──────────────────────────────────────────────────────────────────────── */

export function checkHandleRefs(
  doc: DrawingDocument,
  res: Resolution,
  errors: ReferenceIssue[],
): void {
  // BlockDefinition: ownerHandle → blockRecord handles (P2-B; optional);
  // layer → layers.
  for (const [name, b] of Object.entries(doc.blocks)) {
    checkHandle(
      b.ownerHandle,
      res.handles.blockRecords,
      false,
      ReferenceErrorCode.DANGLING_OWNER_HANDLE,
      {
        ownerKind: "blockDefinition",
        ownerId: name,
        path: "ownerHandle",
        field: "ownerHandle",
      },
      errors,
    );
    checkName(
      b.layer,
      res.names.layers,
      SENT_NONE,
      true,
      ReferenceErrorCode.UNDEFINED_LAYER,
      {
        ownerKind: "blockDefinition",
        ownerId: name,
        path: "layer",
        field: "layer",
      },
      errors,
    );
    // xrefPathName (field 39) is a separate xref axis — intentionally skipped.
    // dynamicMetadata / authoringData refs (field 48) — see TODO below.
    // TODO(PR2): validate dynamic-block dynamicMetadata / authoringData
    // refs (field 48) against each block's child-id set via the existing
    // validateDynamicMetadata; deep authoring-handle integrity deferred.
  }

  // BlockRecord: layoutHandle → layout handles (P2-B; optional);
  // annotativeDecompositions KEYS → annotationScales, VALUES → block
  // (definition) handles (P2-B; F4).
  for (const [name, r] of Object.entries(doc.blockRecords)) {
    checkHandle(
      r.layoutHandle,
      res.handles.layouts,
      false,
      ReferenceErrorCode.DANGLING_HANDLE,
      {
        ownerKind: "blockRecord",
        ownerId: name,
        path: "layoutHandle",
        field: "layoutHandle",
      },
      errors,
    );
    // extensionDictionaryHandle → DICTIONARY (raw) — unverifiable, skipped.
    for (const [scaleName, anonHandle] of Object.entries(
      r.annotativeDecompositions,
    )) {
      // 44a — KEY → annotationScales (optional; advisory).
      checkName(
        scaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        {
          ownerKind: "blockRecord",
          ownerId: name,
          path: `annotativeDecompositions['${scaleName}']`,
          field: "annotativeDecompositions.key",
        },
        errors,
      );
      // 44b — VALUE → anon-block (definition) handle (P2-B; optional; empty
      // map OK).
      checkHandle(
        anonHandle,
        res.handles.blocks,
        false,
        ReferenceErrorCode.DANGLING_HANDLE,
        {
          ownerKind: "blockRecord",
          ownerId: name,
          path: `annotativeDecompositions['${scaleName}']`,
          field: "annotativeDecompositions.value",
        },
        errors,
      );
    }
  }

  // Layout: ownerBlockRecordHandle → blockRecord handles (P2-B; required).
  // NB (Codex #199 P2 — verified false positive, kept): a LAYOUT object's
  // group-330 owner is the owning BLOCK_RECORD handle (`*Paper_Space<N>` /
  // `*Model_Space`), NOT the ACAD_LAYOUT dictionary. The importer captures it
  // verbatim into `ownerBlockRecordHandle` (dxfBridge.layouts.ts:128 ←
  // sectionParsers.parseLayoutEntry:1329-1331; group-330 = owning BLOCK_RECORD
  // per the parser's own doc comment ~1257). It reliably resolves to a
  // blockRecord handle in this model — see the fixture's
  // LAYOUT.330 → BLOCK_RECORD.5 pairs (dxfBridge.multilayout.test.ts:10-11,
  // 345-352) and the round-trip BLOCK_RECORD.340 ↔ LAYOUT.5 link. So this
  // required check is correct; do NOT remove it.
  for (const [name, l] of Object.entries(doc.layouts)) {
    checkHandle(
      l.ownerBlockRecordHandle,
      res.handles.blockRecords,
      true,
      ReferenceErrorCode.DANGLING_OWNER_HANDLE,
      {
        ownerKind: "layout",
        ownerId: name,
        path: "ownerBlockRecordHandle",
        field: "ownerBlockRecordHandle",
      },
      errors,
    );
  }

  // Document: cannoscaleName → annotationScales (required).
  checkName(
    doc.cannoscaleName,
    res.names.annotationScales,
    SENT_NONE,
    true,
    ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
    {
      ownerKind: "document",
      ownerId: "document",
      path: "cannoscaleName",
      field: "cannoscaleName",
    },
    errors,
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * Block reference cycles (DFS over block → INSERT graph).
 * ──────────────────────────────────────────────────────────────────────── */

export function checkBlockReferenceCycles(
  doc: DrawingDocument,
  errors: ReferenceIssue[],
): void {
  // Build block → set of child block names (from INSERT children). Keyed on
  // the lowercased name for case-insensitive cycle detection, but we keep an
  // lcName → original-case map so the emitted issue reports the ORIGINAL-case
  // block name (FIX-1): `ownerId`/`referencedKey` must match
  // `document.blocks["MyBlock"]`, not the lowercased "myblock".
  const graph = new Map<string, Set<string>>();
  const originalName = new Map<string, string>();
  for (const [name, b] of Object.entries(doc.blocks)) {
    const key = name.toLowerCase();
    // First definition wins for the display name (matches insertion order).
    if (!originalName.has(key)) originalName.set(key, name);
    let children = graph.get(key);
    if (!children) {
      children = new Set<string>();
      graph.set(key, children);
    }
    collectInsertChildren(b, children);
  }

  // Iterative DFS with a visiting set; report once per cycle entry block.
  const state = new Map<string, 0 | 1 | 2>(); // 0=unvisited,1=visiting,2=done
  const reported = new Set<string>();

  const visit = (start: string): void => {
    // Explicit stack of frames so a deep block tree never overflows.
    const stack: { node: string; iter: Iterator<string> }[] = [];
    const enter = (node: string): boolean => {
      const s = state.get(node) ?? 0;
      if (s === 1) {
        if (!reported.has(node)) {
          reported.add(node);
          // Emit the ORIGINAL-case name (fall back to the key only if a
          // child references a block that has no definition).
          const display = originalName.get(node) ?? node;
          errors.push({
            code: ReferenceErrorCode.INVALID_BLOCK_REFERENCE_CYCLE,
            ownerKind: "blockDefinition",
            ownerId: display,
            path: "entities",
            refKind: "name",
            field: "blockName",
            referencedKey: display,
            message: `block reference cycle through "${display}"`,
          });
        }
        return false;
      }
      if (s === 2) return false;
      state.set(node, 1);
      stack.push({
        node,
        iter: (graph.get(node) ?? new Set<string>()).values(),
      });
      return true;
    };

    if (!enter(start)) return;
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      const next = frame.iter.next();
      if (next.done) {
        state.set(frame.node, 2);
        stack.pop();
        continue;
      }
      enter(next.value);
    }
  };

  for (const name of graph.keys()) {
    if ((state.get(name) ?? 0) === 0) visit(name);
  }
}

export function collectInsertChildren(
  block: BlockDefinition,
  out: Set<string>,
): void {
  for (const child of block.entities) {
    if (child.type === "Block" && child.blockName) {
      out.add(child.blockName.toLowerCase());
    }
  }
}
